/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business スケルトンローディングコンポーネント。text/circular/rectangularの3形状とSkeletonText複数行ヘルパーを提供
 * @graph-connects none
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../../lib/cn';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** 形状 */
  variant?: 'text' | 'circular' | 'rectangular';
  /** 幅（テキストの場合は文字数相当） */
  width?: string | number;
  /** 高さ */
  height?: string | number;
}

/** @graph-connects none */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ variant = 'text', width, height, className, style, ...props }, ref) => {
    const variantClass = {
      text: 'rounded',
      circular: 'rounded-full',
      rectangular: 'rounded-lg',
    }[variant];

    const defaultHeight = variant === 'text' ? '1em' : variant === 'circular' ? width : '100px';

    return (
      <div
        ref={ref}
        className={cn('bg-[var(--color-bg-elevated)] animate-pulse', variantClass, className)}
        style={{
          width: width ?? '100%',
          height: height ?? defaultHeight,
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  },
);

Skeleton.displayName = 'Skeleton';

// Skeleton グループ用のコンポーネント
export interface SkeletonTextProps {
  /** 行数 */
  lines?: number;
  /** 最後の行の幅 */
  lastLineWidth?: string;
}

/** @graph-connects none */
export function SkeletonText({ lines = 3, lastLineWidth = '60%' }: SkeletonTextProps) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? lastLineWidth : '100%'}
          height="1rem"
        />
      ))}
    </div>
  );
}
